import { Link } from 'react-router-dom';
import { useTranslation } from '../../../i18n/useTranslation';
import { useAppDispatch } from '../../../Redux/hooks';
import { setLanguage } from '../../../Redux/uiSlice';
import { useGoogleSignIn } from '../../../Utils/useGoogleSignIn';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { ProductPanel } from '../../SharedArea/ProductPanel/ProductPanel';
import { routes } from '../../../Utils/routes';
import logo from '../../../Assets/Images/logo.png';
import './Login.css';

/**
 * The way into the app, and for anyone arriving at /user directly the first
 * screen of the product. A split entrance rather than a lone card in the
 * middle of the page: the mark and the controls on a quiet top bar, one
 * headline and one action on one side, the product itself on the other.
 *
 * The language toggle is here because this screen is outside the app shell
 * that normally carries it. There is no theme toggle: this screen (like
 * Home, the other entrance) always renders dark, so there is nothing to
 * switch (see AppLayout's `isEntrance`).
 *
 * Sign-in is Google only, as it has always been. Tutors already have accounts
 * against this Firebase project, so a rebuild is not a reason to make anyone
 * sign up again.
 */
export function Login() {
  const { tr, language } = useTranslation();
  const dispatch = useAppDispatch();
  const { signIn, busy, failed } = useGoogleSignIn();

  return (
    <section className="login-page">
      <div className="login-aura" aria-hidden="true" />

      <header className="login-topbar">
        <div className="login-mark">
          <img src={logo} alt="" />
          <span>{tr('appName')}</span>
        </div>
        <div className="login-topbar-actions">
          <button
            type="button"
            className="login-chip"
            onClick={() => dispatch(setLanguage(language === 'he' ? 'en' : 'he'))}
            // The label names the language being switched to, written in that
            // language, so it reads correctly whichever one is currently on.
            aria-label={language === 'he' ? 'English' : 'עברית'}
          >
            {language === 'he' ? 'EN' : 'עב'}
          </button>
        </div>
      </header>

      <div className="login-grid">
        <div className="login-column">
          <h1 className="login-title">{tr('loginTitle')}</h1>
          <p className="login-lead">{tr('loginLead')}</p>

          <div className="entrance-card login-card">
            <button type="button" className="entrance-google" onClick={signIn} disabled={busy}>
              {busy ? <Spinner inline /> : <GoogleMark />}
              <span>{tr('loginWithGoogle')}</span>
            </button>
            {failed && <p className="entrance-error" role="alert">{tr('loginFailed')}</p>}
            <p className="entrance-note">
              <Link to={routes.privacy}>{tr('loginPrivacyMore')}</Link>
            </p>
          </div>

          <Link className="entrance-secondary login-about" to={routes.home}>
            <WindowMark />
            <span>{tr('loginAbout')}</span>
          </Link>
        </div>

        <div className="login-visual">
          <ProductPanel />
        </div>
      </div>
    </section>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

/** An app window. Deliberately symmetrical, so it needs no flip in Hebrew. */
function WindowMark() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="1.7" y="2.7" width="12.6" height="10.6" rx="2.2" />
      <path d="M1.7 6.1h12.6" strokeLinecap="round" />
    </svg>
  );
}
